import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FormTextField } from '../../ui/forms/input-field/text-field/text-field';
import { Button } from '../../ui/buttons/button';
import { Form } from '../../ui/forms/form';
import { LinkStyle } from '../../ui/buttons/external-link';
import { RegisterPayload, useRegister } from '../requests/use-register';
import { SocialAuthSection } from './social-auth-section';
import { AuthLayout } from './auth-layout/auth-layout';
import { Trans } from '../../i18n/trans';
import { FormCheckbox } from '../../ui/forms/toggle/checkbox';
import { CustomMenuItem } from '../../menus/custom-menu';
import { useRecaptcha } from '../../recaptcha/use-recaptcha';
import { StaticPageTitle } from '../../seo/static-page-title';
import { useSettings } from '../../core/settings/use-settings';

export function RegisterPage() {
  const {
    branding,
    registration: { disable },
  } = useSettings();
  const { verify, isVerifying } = useRecaptcha('register');

  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const isWorkspaceRegister = pathname.includes('workspace');
  const isBillingRegister = searchParams.get('redirectFrom') === 'pricing';
  const searchParamsEmail = searchParams.get('email') || undefined;

  const form = useForm<RegisterPayload>({
    defaultValues: { email: searchParamsEmail },
  });
  const register = useRegister(form);

  if (disable) {
    return <Navigate to="/login" replace />;
  }

  let heading = <Trans message="Create a new account" />;
  if (isWorkspaceRegister) {
    heading = (
      <Trans
        values={{ siteName: branding?.site_name }}
        message="To join your team on :siteName, create an account"
      />
    );
  } else if (isBillingRegister) {
    heading = <Trans message="First, let's create your account" />;
  }

  const message = (
    <Trans
      values={{
        a: parts => (
          <Link className={LinkStyle} to="/login">
            {parts}
          </Link>
        ),
      }}
      message="Already have an account? <a>Sign in.</a>"
    />
  );

  let first_name = <Trans message="First name" />
  let last_name = <Trans message="Last name" />
  let email = <Trans message="Email" />
  let password = <Trans message="Password" />
  let cpassword = <Trans message="Confirm password" />
  let address = <Trans message="Address" />
  let city = <Trans message="City" />
  let country = <Trans message="Country" />
  let zip = <Trans message="Zip Code" />

  const lng = (e: 'en' | 'tr') => {
    console.log('e',e);
    //  if (e === 'tr') {
    //    setEmailText('E-posta');
    //    setPasswordText('Şifre');
    //  } else {
    //    setEmailText('Email');
    //    setPasswordText('Password');
    //  }
   };
  //console.log(first.props.message);
  return (
    <AuthLayout heading={heading} message={message} updateLang={lng}>
      <div className="my-auto flex flex-col justify-center px-4 pt-8  md:justify-start lg:w-[28rem]">
        <p className="text-center text-[22px] text-primary font-bold md:text-left md:leading-tight"> <Trans message="Create an Account" /></p>
        <p className="mt-2 text-center text-[16px] md:text-left">
          <Trans message="Already have an account?" />
          <Link to="/login" className="whitespace-nowrap font-semibold text-orange-500"> <Trans message="Signin" /></Link>
        </p>
        <div className="flex items-center space-x-6">
          <button className=" mt-6 flex w-[198px] h-[48px]  items-center justify-center rounded-md border px-4 py-1 outline-none ring-gray-400 ring-offset-2 transition hover:border-transparent hover:bg-black hover:text-white focus:ring-2"><img className="mr-2 h-5" src="./images/google.png" />Google</button>
          <button className="-2 mt-6 flex w-[198px] h-[48px]  items-center justify-center rounded-md border px-4 py-1 outline-none ring-gray-400 ring-offset-2 transition hover:border-transparent hover:bg-black hover:text-white focus:ring-2"><img className="mr-2 h-5" src="./images/facebook.png" />Facebook</button>
        </div>
        <Form
          form={form}
          className="flex flex-col pt-3 md:pt-8"
          onSubmit={async payload => {
            const isValid = await verify();
            if (isValid) {
              register.mutate(payload);
            }
          }}
        >
          <div className="flex flex-col pt-4">
            <div className="relative flex transition focus-within:border-blue-600">
              <FormTextField
                className="w-full rounded-md flex-shrink appearance-none border-gray-300 bg-white py-2 px-4 text-base text-gray-700 placeholder-gray-400 focus:outline-none"
                name="first_name"
                type="text"
                placeholder={first_name.props.message}
                disabled={!!searchParamsEmail}
                required
              />
            </div>
          </div>
          <div className="flex flex-col pt-4">
            <div className="relative flex transition focus-within:border-blue-600">
              <FormTextField
                className="w-full rounded-md flex-shrink appearance-none border-gray-300 bg-white py-2 px-4 text-base text-gray-700 placeholder-gray-400 focus:outline-none"
                name="last_name"
                type="text"
                placeholder={last_name.props.message}
                disabled={!!searchParamsEmail}
                required
              />
            </div>
          </div>
          <div className="flex flex-col pt-4">
            <div className="relative flex transition focus-within:border-blue-600">
              <FormTextField
                className="w-full rounded-md flex-shrink appearance-none border-gray-300 bg-white py-2 px-4 text-base text-gray-700 placeholder-gray-400 focus:outline-none"
                name="email"
                type="email"
                placeholder={email.props.message}
                disabled={!!searchParamsEmail}
                required
              />
            </div>
          </div>
          <div className="flex flex-col pt-4">
            <div className="relative flex transition focus-within:border-blue-600">
              <FormTextField
                className="w-full rounded-md flex-shrink appearance-none border-gray-300 bg-white py-2 px-4 text-base text-gray-700 placeholder-gray-400 focus:outline-none"
                name="address"
                type="text"
                placeholder={address.props.message}
              />
            </div>
          </div>
          <div className="flex flex-col pt-4">
            <div className="relative flex transition focus-within:border-blue-600">
              <FormTextField
                className="w-full rounded-md flex-shrink appearance-none border-gray-300 bg-white py-2 px-4 text-base text-gray-700 placeholder-gray-400 focus:outline-none"
                name="city"
                type="text"
                placeholder={city.props.message}
              />
            </div>
          </div>
          <div className="flex flex-col pt-4">
            <div className="relative flex transition focus-within:border-blue-600">
              <FormTextField
                className="w-full rounded-md flex-shrink appearance-none border-gray-300 bg-white py-2 px-4 text-base text-gray-700 placeholder-gray-400 focus:outline-none"
                name="country"
                type="text"
                placeholder={country.props.message}
              />
            </div>
          </div>
          <div className="flex flex-col pt-4">
            <div className="relative flex transition focus-within:border-blue-600">
              <FormTextField
                className="w-full rounded-md flex-shrink appearance-none border-gray-300 bg-white py-2 px-4 text-base text-gray-700 placeholder-gray-400 focus:outline-none"
                name="zipcode"
                type="text"
                placeholder={zip.props.message}
              />
            </div>
          </div>
          <div className="flex flex-col pt-4">
            <div className="relative flex transition focus-within:border-blue-600">
              <FormTextField
                className="w-full rounded-md  appearance-none border-gray-300 bg-white py-2 px-4  text-base text-gray-700 placeholder-gray-400 focus:outline-none"
                name="password"
                type="password"
                placeholder={password.props.message}
                required
              />
            </div>
          </div>
          <div className="flex flex-col pt-4">
            <div className="relative flex transition focus-within:border-blue-600">
              <FormTextField
                className="w-full rounded-md flex-shrink appearance-none border-gray-300 bg-white py-2 px-4 text-base text-gray-700 placeholder-gray-400 focus:outline-none"
                name="password_confirmation"
                type="password"
                placeholder={cpassword.props.message}
                required
              />
            </div>
          </div>

          <PolicyCheckboxes />

          <Button
            className="mt-6 w-full rounded-lg bg-primary px-4 py-2 text-center text-base font-semibold text-white shadow-md outline-none ring-blue-500 ring-offset-2 transition hover:bg-blue-700 focus:ring-2 "
            type="submit"
            disabled={register.isLoading || isVerifying}
          >
            <Trans message="Continue" />
          </Button>
        </Form>
      </div>
    </AuthLayout>
  );
}

function PolicyCheckboxes() {
  const {
    registration: { policies },
  } = useSettings();

  if (!policies) return null;

  return (
    <div className="mb-32">
      {policies.map(policy => (
        <FormCheckbox
          key={policy.id}
          name={policy.id}
          className="block mb-4"
          required
        >
          <Trans
            message="I accept the :name"
            values={{
              name: (
                <CustomMenuItem
                  unstyled
                  className={() => LinkStyle}
                  item={policy}
                />
              ),
            }}
          />
        </FormCheckbox>
      ))}
    </div>
  );
}